import { useEffect, useRef, useState, useCallback } from "react";
import { Workbox } from "workbox-window";

export interface PwaState {
  needRefresh: boolean;
  offlineReady: boolean;
  registration: ServiceWorkerRegistration | null;
  updateServiceWorker: () => void;
}

let globalRegistration: ServiceWorkerRegistration | null = null;

export function getServiceWorkerRegistration(): ServiceWorkerRegistration | null {
  return globalRegistration;
}

export function usePwaRegistration(): PwaState {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const wbRef = useRef<Workbox | null>(null);
  const reloadingRef = useRef(false);

  const updateServiceWorker = useCallback(() => {
    const wb = wbRef.current;
    if (!wb) return;
    wb.messageSkipWaiting();
  }, []);

  useEffect(() => {
    if (typeof navigator === "undefined") return;
    if (!("serviceWorker" in navigator)) return;

    const wb = new Workbox("/sw.js", { scope: "/" });
    wbRef.current = wb;

    const showSkipWaitingPrompt = () => {
      setNeedRefresh(true);
    };

    wb.addEventListener("waiting", showSkipWaitingPrompt);

    // If an updated SW is already waiting when we register, prompt immediately.
    wb.addEventListener("activated", (event) => {
      if (!event.isUpdate) {
        setOfflineReady(true);
      }
    });

    // D3 + ADR-0007: reload exactly once on an update, never on first
    // install. `workbox-window` sets `isUpdate` only when an updated
    // worker takes control; the first install of this build fires
    // `controlling` with `isUpdate === false`, which we ignore.
    wb.addEventListener("controlling", (event) => {
      if (!event.isUpdate) return;
      if (reloadingRef.current) return;
      reloadingRef.current = true;
      window.location.reload();
    });

    let registration: ServiceWorkerRegistration | null = null;
    void wb.register().then((reg) => {
      registration = reg ?? null;
      globalRegistration = registration;
      if (registration?.waiting) {
        showSkipWaitingPrompt();
      }
    });

    // Update on foreground (D3).
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        registration?.update();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    // Periodic update every 60 min (D3).
    const interval = window.setInterval(() => {
      registration?.update();
    }, 60 * 60 * 1000);

    return () => {
      wb.removeEventListener("waiting", showSkipWaitingPrompt);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(interval);
    };
  }, []);

  return { needRefresh, offlineReady, registration: globalRegistration, updateServiceWorker };
}
